import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  Fragment,
} from "react";
import { useTranslation } from "react-i18next";
import PharmacyService from "../../../../services/PharmacyService";
import AccountService from "../../../../services/AccountService";
import Button from "../../../../components/Button";
import backIcon from "../../../../assets/back_icon.svg";
import { ReactComponent as ExcelIcon } from "../../../../assets/excel.svg";
import { ReactComponent as PDFIcon } from "../../../../assets/pdf.svg";
import { ReactComponent as Funnel } from "../../../../assets/funnel.svg";
import { ReactComponent as ExpandIcon } from "../../../../assets/add-icon-small.svg";
import { ReactComponent as CollapseIcon } from "../../../../assets/subtract-circle-icon.svg";
import { ReactComponent as GroupIcon } from "../../../../assets/brackets-curly.svg";
import DateRangePickerComponent from "../../../../components/DateRangePickerComponent";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import Spacer from "../../../../components/Spacer";
import WaitIndicator from "../../../../components/WaitIndicator";
import ReportService from "../../../../services/ReportService";
import ErrorBanner from "../../../../components/ErrorBanner";
import FilterPopup from "../../../../components/FilterPopup";
import { useKeyPress } from "../../../../components/InputHooks";
import MultiSelectDropDown from "../../../../components/Ant/AntMultiSelectDropdown/AntMultiSelectDropdown";
import { dateSelectionRangeTimeAdjustment } from "../../../../functions/dates";
// import SubstitutePDFReportViewer from "./SubstitutePDFReportViewer";
import SubstitutePDFReportViewer from "./pdfReportGeneration/PDFReportViewer";
import ReportExcelExporter from "../../ReportExcelExporter";
import { getPreferredFieldName } from "../../lookups/getPreferredFieldName";
import { ScreenContext } from "../../../../contexts/ScreenContext";
import { useMediaQuery } from "react-responsive";
import ProductionReportCardList from "../../MobileReportViews/ProductionReport/ProductionReportCardList";
import {
  solveFunction,
  getColumnAverages,
  getColumnSums,
  getDataFields,
} from "../../reportViewerFunctions";
import {
  isValidISO8601UTC,
  formatDateWithMilitaryTime,
} from "../../../../functions/dates.js";
import { deepCopy } from "../../../../functions/utilities.js";
import { getLocalizedDate } from "../../../../functions/localization";
import styles from "../../ReportViewer.module.css";
import customStyles from "./SubstituteReportViewer.module.css";
import "./SubstituteReportViewer.css";

const SubstituteReportViewer = ({
  user,
  reportObj,
  dispensingOrderIdFromOrder,
  dispensingOrderPackagedOn,
  isPreview,
  onClose,
  returnURL,
}) => {
  const { t } = useTranslation("reportViewer");
  const { smallScreen } = useContext(ScreenContext);
  const smScreen = useMediaQuery(smallScreen);
  const [packageIdLabel, setPackageIdLabel] = useState("");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [showTableData, setShowTableData] = useState(false);
  const [enteredBatchId, setEnteredBatchId] = useState(null);
  const [tableWidthWithScroll, setTableWidthWithScroll] = useState("100%");
  const [reportContainerHeight, setReportContainerHeight] = useState("100%");
  const [selectionRange, setSelectionRange] = useState({});
  const [showDateTimeRangePicker, setShowDateTimeRangePicker] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState("");
  const [numSelectedRows, setNumSelectedRows] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]); // a selected row (array element) is assigned a value of 1; 0 if not selected.
  const [reportData, setReportData] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [showFilter, setShowFilter] = useState(null);
  const [filterList, setFilterList] = useState([]);
  const [sortPreference, setSortPreference] = useState({
    colIndex: 0,
    ascending: true,
  });
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [exportToExcel, setExportToExcel] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [columnSums, setColumnSums] = useState([]);
  const [columnAverages, setColumnAverages] = useState([]);
  const [functionNames, setFunctionNames] = useState([]);
  const [orderedFunctionNames, setOrderedFunctionNames] = useState([]);
  const [functionLookup, setFunctionLookup] = useState({});
  const [functionLookupComplete, setFunctionLookupComplete] = useState(false);
  const [showAVGRow, setShowAVGRow] = useState(false);
  const [showSUMRow, setShowSUMRow] = useState(false);
  const [runReport, setRunReport] = useState(false);
  const [collectionSequence, setCollectionSequence] = useState([]);
  const [largeViewData, setLargeViewData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [groupedDataColumnSums, setGroupedDataColumnSums] = useState([]);
  const [columnDataTypes, setColumnDataTypes] = useState([]);
  const [selectedGroupRowIndex, setSelectedGroupRowIndex] = useState(-1);
  const [expansionActive, setExpansionActive] = useState(false);
  const [groupedField, setGroupedField] = useState("No grouping");
  const [activeGroupIconIndex, setActiveGroupIconIndex] = useState(null);
  const [selectableBatches, setSelectableBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [sites, setSites] = useState([]);
  const [reportAutomaticallyRun, setReportAutomaticallyRun] = useState(false);
  const [primaryFilterColumn, setPrimaryFilterColumn] = useState([]);
  const [lastFilterIndexDeselected, setLastFilterIndexDelelected] =
    useState(-1);
  const [lastFilterColumnIndexClicked, setLastFilterColumnIndexClicked] =
    useState(-1);

  const addedWidthAdjustment = 34;

  const keyPressed = useKeyPress("Escape");
  if (showFilter && keyPressed) {
    setShowFilter(null);
  }

  const combinedReportFieldsRegular = [
    ...reportObj.fields,
    ...reportObj.functionFields,
  ].slice(3);

  let combinedReportFields = combinedReportFieldsRegular.map((field, index) => {
    let friendlyName;
    switch (index) {
      case 0:
        friendlyName = "Prev Drug Name";
        break;
      case 1:
        friendlyName = packageIdLabel
          ? `Prev Drug ${packageIdLabel}`
          : "Prev Drug Id";
        break;
      case 2:
        friendlyName = "Prev Str";
        break;
      case 3:
        friendlyName = "Prev Drug MFG";
        break;
      case 4:
        friendlyName = "Prev Qty";
        break;
      case 5:
        friendlyName = "Sub Drug Name";
        break;
      case 6:
        friendlyName = packageIdLabel
          ? `Sub Drug ${packageIdLabel}`
          : "Sub Drug Id";
        break;
      case 7:
        friendlyName = "Sub Str";
        break;
      case 8:
        friendlyName = "Sub Drug MFG";
        break;
      case 9:
        friendlyName = "Sub Qty";
        break;
      case 10:
        friendlyName = "Rx #";
        break;
      case 11:
        friendlyName = "Patient Name";
        break;
      case 12:
        friendlyName = "Order ID";
        break;
      case 13:
        friendlyName = "Device Name";
        break;
      case 14:
        friendlyName = "Date";
        break;
      default:
        friendlyName = null;
    }
    return { ...field, name: friendlyName };
  });

  const fetchAccount = async () => {
    try {
      const account = await AccountService.get(user);

      if (account?.fdbSettings?.showNDC) {
        setPackageIdLabel("NDC");
      } else if (account?.fdbSettings?.showDIN) {
        setPackageIdLabel("DIN");
      } else {
        setPackageIdLabel("");
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAccount();
  }, []);

  useEffect(() => {
    if (primaryFilterColumn.length) {
      setColumnFilters((prevState) => {
        return prevState.map((filterColumnArray) => {
          return filterColumnArray.map((filterColumn) => {
            if (
              filterColumn.hasOwnProperty("show") &&
              filterColumn.show &&
              !filterColumn.selected &&
              filterColumnArray[0].hasOwnProperty("show") &&
              filterColumnArray[0].show &&
              filterColumnArray[0].selected &&
              filterColumnArray[0].description === filterColumn.description
            ) {
              filterColumn.selected = true;
            }
            return filterColumn;
          });
        });
      });
    }
  }, [primaryFilterColumn]);

  useEffect(() => {
    if (!primaryFilterColumn.length) {
      setColumnFilters((prevState) => {
        return prevState.map((filterColumnArray) => {
          return filterColumnArray.map((filterColumn) => {
            filterColumn.selected = false;
            filterColumn.show = true;
            return filterColumn;
          });
        });
      });
    }
  }, [primaryFilterColumn]);

  useEffect(() => {
    const updateColumnFilters = () => {
      if (primaryFilterColumn.length) {
        setColumnFilters((prevState) => {
          return prevState.map((colArray) => {
            colArray.map((obj) => {
              if (
                obj.hasOwnProperty("show") &&
                obj.show &&
                obj.selected &&
                lastFilterColumnIndexClicked === obj.columnFiltersIndex
              ) {
                obj.show = false;
                obj.selected = false;
              }
              return obj;
            });
            return colArray;
          });
        });
      }
    };
    if (
      primaryFilterColumn.length &&
      primaryFilterColumn[0].index === lastFilterIndexDeselected
    ) {
      updateColumnFilters();
    }
  }, [
    lastFilterIndexDeselected,
    primaryFilterColumn,
    lastFilterColumnIndexClicked,
  ]);

  useEffect(() => {
    const updateColumnFilters2 = () => {
      if (primaryFilterColumn.length) {
        setColumnFilters((prevState) => {
          return prevState.map((colArray) => {
            colArray.map((obj) => {
              if (
                obj.hasOwnProperty("show") &&
                obj.show &&
                !obj.selected &&
                obj.hasOwnProperty("columnFiltersIndex")
              ) {
                colArray.forEach((colArrayObj) => {
                  if (!colArrayObj.hasOwnProperty("columnFiltersIndex")) {
                    if (colArrayObj.description === obj.description) {
                      colArrayObj.selected = false;
                    }
                  }
                });
              }
              return obj;
            });
            return colArray;
          });
        });
      }
    };
    updateColumnFilters2();
  }, [
    lastFilterIndexDeselected,
    primaryFilterColumn,
    lastFilterColumnIndexClicked,
  ]);

  useEffect(() => {
    if (columnFilters && columnFilters.length && reportData) {
      reportData
        .map((dataIndex) => {
          if (isFiltered(dataIndex)) {
            return combinedReportFields.map((reportObject, index) => {
              let dataFieldText = columnFilters[index][dataIndex]?.description;
              return dataFieldText;
            });
          }
        })
        // strip any undefined entries from the array
        .filter((data) => {
          if (data) {
            return selectedBatches?.includes(data[12]);
          }
          return false;
        });
    }

    if (primaryFilterColumn?.length) {
      let filterObjsToShow = [];
      let copiedColumnFilters = columnFilters;

      const primaryIndex = primaryFilterColumn[0].index;
      const reportDataCopy = deepCopy(
        reportData.map((reportDataArray) => {
          return reportDataArray.map((item) => {
            if (item.constructor === Array) {
              item = item[0];
            }
            if (isValidISO8601UTC(item)) {
              item = formatDateWithMilitaryTime(new Date(item));
            }
            return item;
          });
        }),
      );

      const primaryReportData = reportDataCopy
        .map((dataRow) => dataRow)
        .filter((dataRow) => {
          const isObjectInArray = primaryFilterColumn.some((obj) => {
            return obj.description === dataRow[primaryIndex] && obj.selected;
          });
          return isObjectInArray;
        });

      // populate filterObjsToShow for primary
      copiedColumnFilters.forEach((objArray, objArrayIndex) => {
        if (objArrayIndex === primaryFilterColumn[0].index) {
          objArray.forEach((obj, objIndex) => {
            filterObjsToShow.push({
              index: objArrayIndex,
              objIndex: objIndex,
            });
          });
        }

        const secondaryReportData = primaryReportData
          .map((dataRow) => dataRow)
          .filter((dataRow) => {
            const isObjectInArray = primaryFilterColumn.some((obj) => {
              if (dataRow[primaryIndex].constructor === Array) {
                return (
                  obj.description === dataRow[primaryIndex][0] && obj.selected
                );
              }
              return obj.description === dataRow[primaryIndex] && obj.selected;
            });
            return isObjectInArray;
          });

        // populate filterObjsToShow for secondary
        // console.log("secondaryReportData: ", secondaryReportData);
        objArray.forEach((obj, colIndex) => {
          secondaryReportData.forEach((tableRow, tableRowIndex) => {
            if (obj.description.constructor === Array) {
              obj.description = obj.description[0];
            }
            if (
              obj.description === tableRow[objArrayIndex] &&
              objArrayIndex !== primaryFilterColumn[0].index
            ) {
              filterObjsToShow.push({
                index: objArrayIndex,
                objIndex: colIndex,
              });
            }
          });
        });
      });

      setColumnFilters((prevState) => {
        let tempColumnFilters = [];
        let currentIndex = 0;

        let objsToShow = [];
        filterObjsToShow.forEach((objToShow) => {
          let colIndex = objToShow.index;
          let objIndex = objToShow.objIndex;
          if (colIndex === currentIndex) {
            objsToShow.push(prevState[colIndex][objIndex]);
            tempColumnFilters[currentIndex] = objsToShow;
          } else {
            objsToShow = [];
            currentIndex = colIndex;
            objsToShow.push(prevState[colIndex][objIndex]);
            tempColumnFilters[currentIndex] = objsToShow;
          }
        });

        return prevState.map((item, colIndex) => {
          item.map((col) => {
            const isObjectInArray = tempColumnFilters[colIndex]?.some((obj) => {
              return obj.description === col.description;
            });
            if (isObjectInArray) {
              col["show"] = true;
            } else {
              col["show"] = false;
            }
            return col;
          });
          return item;
        });
      });
    }
  }, [primaryFilterColumn, reportData, selectedBatches]);

  useEffect(() => {
    if (
      !reportAutomaticallyRun &&
      dispensingOrderIdFromOrder &&
      Object.keys(selectionRange).length !== 0
    ) {
      handleRunReport(0, 0);
      setReportAutomaticallyRun(true);
      setInitialDataLoaded(true);
    }
  }, [dispensingOrderIdFromOrder, selectionRange, reportAutomaticallyRun]);

  useEffect(() => {
    if (!dispensingOrderIdFromOrder) {
      setEnteredBatchId("-1");
    }
  }, [dispensingOrderIdFromOrder]);

  useEffect(() => {
    PharmacyService.list(user).then((sites) => {
      let filteredSites = [];
      if (user.isEnterprise) {
        sites.sort((a, b) => {
          if (a.name.toUpperCase() < b.name.toUpperCase()) {
            return -1;
          }
          if (b.name.toUpperCase() < a.name.toUpperCase()) {
            return 1;
          }
          return 0;
        });

        filteredSites = sites;

        setSites(filteredSites);
      } else {
        filteredSites = sites
          .filter((site) => user.sites.some((o) => o._id === site._id))
          .map((site) => site);

        filteredSites.sort((a, b) => {
          if (a.name.toUpperCase() < b.name.toUpperCase()) {
            return -1;
          }
          if (b.name.toUpperCase() < a.name.toUpperCase()) {
            return 1;
          }
          return 0;
        });

        setSites(filteredSites);
      }
    });
  }, [user]);

  useEffect(() => {
    let selectableBatchIds = [];
    for (const dataRow of reportData) {
      if (!selectableBatchIds?.includes(dataRow[12])) {
        selectableBatchIds.push(dataRow[12]);
      }
    }
    let selectableTempBatches = [];

    for (const batchId of selectableBatchIds) {
      if (dispensingOrderIdFromOrder) {
        selectableTempBatches.push({ id: batchId, label: batchId });
      } else if (enteredBatchId) {
        selectableTempBatches.push({ id: batchId, label: batchId });
      }
    }

    const sortedSelectableBatches = selectableTempBatches.sort(function (a, b) {
      if (a.id > b.id) {
        return -1;
      }
      if (b.id > a.id) {
        return 1;
      }
      return 0;
    });

    setSelectableBatches(sortedSelectableBatches);
  }, [reportData, dispensingOrderIdFromOrder, enteredBatchId]);

  useEffect(() => {
    if (selectableBatches.length) {
      let tempSelectedBatches = [];
      for (const batchId of selectableBatches) {
        if (
          enteredBatchId === "-1" ||
          (enteredBatchId && batchId.id === enteredBatchId) ||
          (dispensingOrderIdFromOrder &&
            batchId.id === dispensingOrderIdFromOrder)
        )
          tempSelectedBatches.push(batchId.id);
      }
      setSelectedBatches((prevState) => {
        if (!prevState.length) {
          return tempSelectedBatches;
        } else {
          return prevState;
        }
      });
    }
  }, [selectableBatches, dispensingOrderIdFromOrder, enteredBatchId]);

  useEffect(() => {
    let tempGroupedColumnSums = [];
    groupedData.forEach((groupedRows, groupIndex) => {
      tempGroupedColumnSums.push([]);
      tempGroupedColumnSums[groupIndex].push([]);
      groupedRows.forEach(
        (tempGroupedColumnSumsRow, tempGroupedColumnSumsRowIndex) => {
          tempGroupedColumnSumsRow.forEach((datum, datumIndex) => {
            if (columnDataTypes[datumIndex] === "Number") {
              if (tempGroupedColumnSumsRowIndex === 0) {
                if (isNaN(datum)) {
                  tempGroupedColumnSums[groupIndex][datumIndex] = 0;
                } else {
                  tempGroupedColumnSums[groupIndex][datumIndex] = datum;
                }
              } else if (!isNaN(datum) && datum !== "") {
                tempGroupedColumnSums[groupIndex][datumIndex] += datum;
              }
            } else {
              if (tempGroupedColumnSumsRowIndex === 0) {
                tempGroupedColumnSums[groupIndex][datumIndex] = null;
              }
            }
          });
        },
      );
    });
    setGroupedDataColumnSums(tempGroupedColumnSums);
  }, [groupedData, columnDataTypes]);

  useEffect(() => {
    setReportContainerHeight(document.body.clientHeight - 320);
    let onresize = function () {
      setReportContainerHeight(document.body.clientHeight - 320);
    };
    window.addEventListener("resize", onresize);
    return () => window.removeEventListener("resize", onresize);
  }, []);

  useEffect(() => {
    if (!smScreen) {
      // Use a setTimeout to make sure the table width is updated before getting its width.
      setTimeout(() => {
        let element = document.querySelector("#tableContainer");
        let widthWithoutScroll = element?.offsetWidth;
        let widthWithScroll = element?.scrollWidth;
        setTableWidthWithScroll(
          widthWithScroll > widthWithoutScroll ? widthWithScroll : "100%",
        );
      }, "1000");
    }
  }, [reportData, smScreen]);

  useEffect(() => {
    let startDate;
    let endDate;

    if (dispensingOrderPackagedOn) {
      // dispensingOrderPackagedOn is in UTC
      startDate = new Date(dispensingOrderPackagedOn); // creates a Date object using the local time zone of the system where the code is running
      // console.log("startDate in UTC ", startDate.toISOString());
      // console.log("startDate in Local: ", startDate);
      endDate = new Date(dispensingOrderPackagedOn);
    } else {
      startDate = new Date();
      endDate = new Date();
    }

    setSelectionRange(dateSelectionRangeTimeAdjustment(startDate, endDate));

    const collections = reportObj.fields.map((field) => {
      return field.collection;
    });

    setCollectionSequence(collections);
  }, [reportObj]);

  useEffect(() => {
    if (reportObj?.groupedField && reportObj.groupedField !== "No grouping") {
      const groupedFieldIndex = reportObj.fields.findIndex((field) => {
        return `${field.collection}:${field.name}` === reportObj.groupedField;
      });
      if (groupedFieldIndex !== -1) {
        setActiveGroupIconIndex(groupedFieldIndex);
      }
    }
  }, []);

  // determine the maximum width of each column
  useEffect(() => {
    let maximumWidth = 40; // characters - not pixels
    let widths = [];
    for (let i = 0; i < columnFilters.length; i++) {
      let colFilter = columnFilters[i];

      let fieldName;
      if (typeof combinedReportFields[i] === "string") {
        fieldName = combinedReportFields[i];
      } else {
        fieldName = getPreferredFieldName(
          combinedReportFields[i]?.collection,
          combinedReportFields[i]?.name,
          false,
          false,
          "header",
        );
      }

      let maxWidth = fieldName?.length;
      for (let j = 0; j < colFilter.length; j++) {
        let fieldValue = colFilter[j]?.description;
        if (fieldValue.length > maxWidth) {
          maxWidth = fieldValue.length;
        }
      }

      if (maxWidth > maximumWidth) {
        maxWidth = maximumWidth;
      }
      widths.push(maxWidth * 10 + 12);
    }
    setColumnWidths(widths);
  }, [columnFilters]);

  useEffect(() => {
    let rowsSelected = 0;
    let tempSelectedRows = [];

    for (let i = 0; i < reportData.length; i++) {
      tempSelectedRows[i] = 0;
    }

    for (let row = 0; row < columnFilters.length; row++) {
      let rowSelected = false;
      columnFilters[row].forEach((columnFilter, columnFilterIndex) => {
        if (columnFilter.selected) {
          if (!rowSelected) {
            rowsSelected++;
          }
          tempSelectedRows[columnFilterIndex] = 1;
        }
      });

      if (rowsSelected === 0) {
        setNumSelectedRows(reportData.length);
      } else {
        setNumSelectedRows(rowsSelected);
      }
    }

    if (rowsSelected === 0) {
      tempSelectedRows.forEach((element, index) => {
        tempSelectedRows[index] = 1;
      });
    }

    setSelectedRows(tempSelectedRows);
  }, [columnFilters, reportData]);

  const formatDate = (item) => {
    if (item) {
      const date = new Date(item);
      return getLocalizedDate(
        date,
        user?.defaultSite?.shippingAddress?.country,
        true,
      );
    }
    return "";
  };

  useEffect(() => {
    if (
      (functionLookupComplete || orderedFunctionNames.length === 0) &&
      reportData
    ) {
      getDataFields(
        combinedReportFields,
        reportObj,
        reportData,
        formatDate,
        functionLookup,
        handleColumnFilters,
      );
    }
  }, [
    functionLookupComplete,
    reportData,
    orderedFunctionNames,
    reportObj,
    functionLookup,
  ]);

  useEffect(() => {
    let tempFunctionNames = [];
    combinedReportFields.forEach((field) => {
      if (field.collection === "function") {
        tempFunctionNames.push(field.name);
      }
    });
    setFunctionNames(tempFunctionNames);
  }, []);

  // Creates and sets columnSums (state) and ColumnAverages (state)
  useEffect(() => {
    if (numSelectedRows > 0) {
      setColumnSums(
        getColumnSums(
          combinedReportFields,
          reportData,
          functionLookup,
          selectedRows,
          reportObj,
        ),
      );
      setColumnAverages(
        getColumnAverages(
          combinedReportFields,
          reportData,
          functionLookup,
          selectedRows,
          numSelectedRows,
          reportObj,
        ),
      );
    }
  }, [reportData, selectedRows, functionLookup, numSelectedRows, reportObj]);

  useEffect(() => {
    if (columnFilters.length > 0 && columnAverages.length > 0) {
      let show = false;

      for (let i = 0; i < columnAverages.length; i++) {
        if (columnAverages[i] >= 0) {
          show = true;
          break;
        }
      }
      setShowAVGRow(show);
    }
  }, [columnFilters, columnAverages]);

  useEffect(() => {
    if (columnFilters.length > 0 && columnSums.length > 0) {
      let show = false;

      for (let i = 0; i < columnSums.length; i++) {
        if (columnSums[i] >= 0) {
          show = true;
          break;
        }
      }
      setShowSUMRow(show);
    }
  }, [columnFilters, columnSums]);

  // Assigns to functionLookup a key which cooresponds to each function name and intializes each value to an empty array ([])
  useEffect(() => {
    if (functionNames.length > 0) {
      let functionsHolder = [...reportObj.functionFields];
      let tempOrderedFunctionNames = [];

      while (
        tempOrderedFunctionNames.length < reportObj.functionFields.length
      ) {
        for (let i = 0; i < functionsHolder.length; i++) {
          let place = false;
          reportObj.functionFields.forEach((field) => {
            if (
              !tempOrderedFunctionNames.includes(field.name) &&
              (tempOrderedFunctionNames.includes(field.operands[0].name) ||
                !functionNames.includes(field.operands[0].name)) &&
              (tempOrderedFunctionNames.includes(field.operands[1].name) ||
                !functionNames.includes(field.operands[1].name))
            ) {
              place = true;
            }
            if (place) {
              tempOrderedFunctionNames.push(field.name);
              place = false;
              setOrderedFunctionNames(tempOrderedFunctionNames);
            }
          });
        }
      }
      let tempFunctionLookup = {};
      tempOrderedFunctionNames.forEach((name) => {
        tempFunctionLookup[name] = [];
      });
      setFunctionLookup(tempFunctionLookup);
    }
  }, [functionNames, reportObj]);

  useEffect(() => {
    // Assigns a value to each key in functionLookup and subsequently sets functionLookupComplete to true
    if (
      !functionLookupComplete &&
      Object.keys(functionLookup).length > 0 &&
      reportData.length > 0
    ) {
      let tempFunctionLookup;
      reportData.forEach((rowData, rowDataIndex) => {
        orderedFunctionNames.forEach((functionName, functionIndex) => {
          let fieldIndex = reportObj.functionFields.findIndex(
            (o) => o.name === functionName,
          );
          let dataFieldText = solveFunction(
            reportObj.functionFields[fieldIndex],
            rowData,
            combinedReportFields,
            rowDataIndex,
            combinedReportFields,
            getPathValue,
            functionLookup,
          );
          tempFunctionLookup = { ...functionLookup };
          tempFunctionLookup[functionName][rowDataIndex] = dataFieldText;
        });
      });
      setFunctionLookupComplete(true);
      setFunctionLookup(tempFunctionLookup);
    }
  }, [
    functionLookup,
    reportData,
    combinedReportFields,
    functionLookupComplete,
    orderedFunctionNames,
    reportObj,
  ]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutsidePopupZone);

    return () => {
      document.removeEventListener("click", handleClickOutsidePopupZone);
    };
  });

  useEffect(() => {
    if (reportObj?.groupedField) {
      setGroupedField(reportObj.groupedField);
    }
  }, [reportObj]);

  const isFiltered = (dataIndex) => {
    // isFiltered return true if there are no selected filteres or a selected filter matches
    // this data element - look in each column
    let j = 0;
    for (let col = 0; col < combinedReportFields.length; col++) {
      let columnFilter = columnFilters[col];
      let description = columnFilter[dataIndex]?.description || "";
      if (
        columnFilter.some((o) => o.selected) &&
        !columnFilter.some((o) => o.description === description && o.selected)
      ) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    {
      if (columnFilters && columnFilters.length && reportData) {
        const filteredLargeViewData = reportData
          .map((dataRow, dataIndex) => {
            if (isFiltered(dataIndex)) {
              return combinedReportFields.map((reportObject, index) => {
                let dataFieldText =
                  columnFilters[index][dataIndex]?.description;
                return dataFieldText;
              });
            }
          })
          // strip any undefined entries from the array
          .filter((data) => {
            if (data) {
              return selectedBatches?.includes(data[12]);
            }
            return false;
          });
        setLargeViewData(filteredLargeViewData);
      }
    }
  }, [columnFilters, reportData, selectedBatches]);

  useEffect(() => {
    if (largeViewData.length) {
      // const groupedField = reportObj.groupedField;

      const columnIndex = reportObj.fields.findIndex((fieldObject) => {
        return `${fieldObject.collection}:${fieldObject.name}` === groupedField;
      });

      let searched = [];
      let tempGroupedData = [];
      let tempGroupedDataIndex = 0;
      if (largeViewData && largeViewData?.length && largeViewData) {
        largeViewData.forEach((dataRowFirstLevel) => {
          if (
            dataRowFirstLevel &&
            columnIndex >= 0 &&
            !searched.includes(dataRowFirstLevel[columnIndex])
          ) {
            searched.push(dataRowFirstLevel[columnIndex]);
            largeViewData.forEach((dataRowSecondLevel) => {
              if (
                dataRowSecondLevel &&
                dataRowFirstLevel[columnIndex] ===
                  dataRowSecondLevel[columnIndex]
              ) {
                if (tempGroupedData.length - 1 >= tempGroupedDataIndex) {
                  tempGroupedData[tempGroupedDataIndex].push(
                    dataRowSecondLevel,
                  );
                } else {
                  tempGroupedData[tempGroupedDataIndex] = [dataRowSecondLevel];
                }
              }
            });
            tempGroupedDataIndex++;
          }
        });
        setGroupedData(tempGroupedData);
      }
    }
  }, [largeViewData, groupedField, reportObj]);

  const handleClickOutsidePopupZone = () => {
    // Automatically closes all popups if a click is made outside of the popup zone.
    setShowFilter(null);
  };

  const handleClickInsidePopupZone = (event) => {
    // Stops the mouse-click event from bubbling up. If a click is made inside of the popup zone, this prevents a document click from calling handleClickOutsidePopupZone.
    event.stopPropagation();
  };

  const handleDateRangeSelect = (ranges) => {
    const startDate = new Date(ranges.selection.startDate);
    const endDate = new Date(ranges.selection.endDate);
    setSelectionRange(dateSelectionRangeTimeAdjustment(startDate, endDate));
  };

  const handleRunReport = useCallback(
    (pageSize = 0, skip = 0) => {
      setShowTableData(false);
      setFetchingData(true);
      const machineTypeFilter =
        returnURL === "/dashboard" ? "TruScript" : "null";

      const query = {
        startDate: selectionRange.startDate,
        stopDate: selectionRange.endDate,
        isPreview: isPreview,
        reportObj: reportObj,
        machineTypeFilter: machineTypeFilter,
        pageSize: Math.abs(pageSize),
        skip: Math.abs(skip),
      };

      // Get report data
      ReportService.run2(user, query)
        .then((results1) => {
          let results1NoRepeats = [];

          for (const dataRow of results1[0]) {
            // dataRow[6] = dataRow[2] - dataRow[11];
            dataRow[7] = dataRow[2];
            let addRow = true;
            for (let i = 0; i < results1NoRepeats.length; i++) {
              if (
                dataRow[4] === results1NoRepeats[i][4] && // drug Id
                dataRow[13] === results1NoRepeats[i][13] && // Rx#
                dataRow[15] === results1NoRepeats[i][15] // dispensing order Id
              ) {
                addRow = false;
                if (dataRow[0]) {
                  results1NoRepeats[i][12] += dataRow[12]; // sum prev Qty (packagedItem.quantity)
                }
                results1NoRepeats[i][7] += dataRow[7]; // sum qtyNeed (packagedItem.dipensingOrderItem.qtyNeeded)
                break;
              }
            }
            if (addRow) {
              if (
                dataRow[1].toLowerCase() === "packaged" ||
                dataRow[1].toLowerCase() === "delivered"
              ) {
                if (!dataRow[0]) {
                  dataRow[12] = 0;
                }
                results1NoRepeats.push(dataRow);
              }
            }
          }

          let tempResults = [...results1NoRepeats];
          let resultsWithoutIsSubstituteColumn = [];

          for (const dataRow of tempResults) {
            let newDataRow = dataRow.slice(3);
            resultsWithoutIsSubstituteColumn.push(newDataRow);
          }

          setReportData(resultsWithoutIsSubstituteColumn);
          setRunReport(true);
          setShowTableData(true);
          setFetchingData(false);
        })
        .catch((error) => {
          setError(error);
          setFetchingData(false);
        });
    },
    [
      dispensingOrderIdFromOrder,
      isPreview,
      reportObj,
      returnURL,
      selectionRange,
      user,
    ],
  );

  useEffect(() => {
    if (isPreview && reportObj?.ignoreDateRange) {
      handleRunReport(0, 0);
    }
  }, [isPreview, reportObj, handleRunReport]);

  const handleColumnFilters = (val) => {
    setColumnFilters(val);
  };

  const getPathValue = (path, data) => {
    let val = "";
    const parts = path.split(".");
    for (let i = 0; i < parts.length; i++) {
      if (!val) {
        val = data[`${parts[i]}`];
      } else {
        val = val[`${parts[i]}`];
      }
    }
    return val;
  };

  const handleFilterClick = (e, column, width) => {
    if (columnFilters && columnFilters.length) {
      const list = columnFilters[column].filter((column, columnIndex) => {
        if (
          selectedBatches?.includes(columnFilters[12][columnIndex].description)
        ) {
          return true;
        }
        return false;
      });

      if (list && list.length) {
        setFilterList(list);
        var rect = e.target.getBoundingClientRect();
        setShowFilter(
          showFilter
            ? null
            : { left: e.clientX, topaverage: e.clientY + rect.height * 2 },
        );
      }
    }
  };

  const handleGroupClick = (index) => {
    const fieldToGroup = `${reportObj.fields[index].collection}:${reportObj.fields[index].name}`;
    setGroupedField(fieldToGroup);
    setExpansionActive(false);
    setSelectedGroupRowIndex(-1);
    setActiveGroupIconIndex(index);
  };

  const handleResetToDefault = () => {
    // Clear filters
    setShowFilter(null);
    setFilterList([]);
    setPrimaryFilterColumn([]);
    setLastFilterIndexDelelected(-1);
    setLastFilterColumnIndexClicked(-1);

    // Set grouping to default
    if (reportObj?.groupedField) {
      setGroupedField(reportObj.groupedField);
      setExpansionActive(false);
      setSelectedGroupRowIndex(-1);
      if (reportObj?.groupedField && reportObj.groupedField !== "No grouping") {
        const groupedFieldIndex = reportObj.fields.findIndex((field) => {
          return `${field.collection}:${field.name}` === reportObj.groupedField;
        });
        if (groupedFieldIndex !== -1) {
          setActiveGroupIconIndex(groupedFieldIndex);
        }
      } else {
        setActiveGroupIconIndex(null);
      }
    } else {
      handleNoFieldGrouping();
    }
  };

  const handleNoFieldGrouping = () => {
    setGroupedField("No grouping");
    setActiveGroupIconIndex(null);
    setExpansionActive(false);
    setSelectedGroupRowIndex(-1);
  };

  const handleNoFilters = () => {
    setColumnFilters((prevState) => {
      return prevState.map((filterColumnArray) => {
        return filterColumnArray.map((filterColumn) => {
          filterColumn.selected = false;
          filterColumn.show = true;
          return filterColumn;
        });
      });
    });
    setPrimaryFilterColumn([]);
    setLastFilterIndexDelelected(-1);
    setLastFilterColumnIndexClicked(-1);
  };

  const getColumnSumAndAverage = (col) => {
    let sumAndAverageObject = { sum: "", average: "" };
    let sum = 0;
    let columnFilter = columnFilters[col];
    for (let i = 0; i < columnFilter?.length; i++) {
      if (isFiltered(i)) {
        let val = columnFilter[i].description;
        sum += Number(val);
      }
    }
    let average = sum / numSelectedRows;
    sumAndAverageObject.sum = sum;
    sumAndAverageObject.average = average;
    return { ...sumAndAverageObject };
  };

  const getFunnelColor = (col) => {
    let columnFilter = columnFilters[col];
    if (
      columnFilter &&
      columnFilter.some((o) => o.selected) &&
      primaryFilterColumn.length &&
      primaryFilterColumn[0].index === col
    ) {
      return "#FFAB2B";
    }
    if (columnFilter && columnFilter.some((o) => o.selected)) {
      return "#089BAB";
    }
    return "#778CA2";
  };

  const toggleSortOrder = (colIndex) => {
    let pref = { ...sortPreference };
    if (colIndex === pref.colIndex) {
      pref.ascending = !pref.ascending;
    } else {
      pref.colIndex = colIndex;
      pref.ascending = false;
    }
    setSortPreference(pref);
    sortData(pref);
  };

  const sortData = (pref) => {
    let rows = [];
    for (let i = 0; i < reportData.length; i++) {
      let row = [];
      for (let colIndex = 0; colIndex < columnFilters.length; colIndex++) {
        row.push(columnFilters[colIndex][i]);
      }
      rows.push(row);
    }
    rows.sort((a, b) => {
      if (a[pref.colIndex].description < b[pref.colIndex].description) {
        return pref.ascending ? -1 : 1;
      }
      if (b[pref.colIndex].description < a[pref.colIndex].description) {
        return pref.ascending ? 1 : -1;
      }
      return 0;
    });
    transposeData(rows);
  };

  const transposeData = (rows) => {
    let cols = [];
    for (let i = 0; i < reportData.length; i++) {
      let row = rows[i];
      for (let rowIndex = 0; rowIndex < row.length; rowIndex++) {
        if (cols[rowIndex]) {
          cols[rowIndex] = cols[rowIndex].concat(row[rowIndex]);
        } else {
          cols.push([row[rowIndex]]);
        }
      }
    }
    setColumnFilters(cols);
  };

  const handleLastFilterIndexDelelected = (index) => {
    setLastFilterIndexDelelected(index);
  };

  const NormalTableHeadings = (obj, width, index) => {
    const increasedWidth = width + addedWidthAdjustment;
    return (
      <div
        className={styles.ReportViewer__reportHeaderGroup}
        key={index}
        style={{
          minWidth: `${increasedWidth}px`,
          maxWidth: `${increasedWidth}px`,
          minHeight: "20px",
        }}
      >
        <p
          className={styles.ReportViewer__reportHeaderText}
          onClick={() => toggleSortOrder(index)}
        >
          {index <= reportObj.fields.length - 1 ? (
            `${getPreferredFieldName(
              obj.collection,
              obj.name,
              false,
              false,
              "header",
            )}`
          ) : (
            <span className={styles.ReportViewer__tooltip}>
              {obj.name}
              <span className={styles.ReportViewer__tooltiptext}>
                <span className={styles.ReportViewer__tooltipSpan}>
                  {getPreferredFieldName(
                    obj.operands[0].collection,
                    obj.operands[0].name,
                    false,
                    false,
                  )}
                </span>
                <span className={styles.ReportViewer__tooltipSpan}>
                  {obj.operator.description}
                </span>
                <span className={styles.ReportViewer__tooltipSpan}>
                  {getPreferredFieldName(
                    obj.operands[1].collection,
                    obj.operands[1].name,
                    false,
                    false,
                  )}
                </span>
              </span>
            </span>
          )}
        </p>
        <div
          onClick={(event) => handleClickInsidePopupZone(event)}
          style={{ cursor: "pointer" }}
        >
          <Funnel
            stroke={getFunnelColor(index)}
            onMouseOver={(e) => {
              e.target.fill = "#089BAB";
            }}
            onClick={(e) =>
              handleFilterClick(e, index, 1000 / combinedReportFields.length)
            }
          />
        </div>
        <div
          className={
            activeGroupIconIndex === index
              ? `${styles.ReportViewer__groupIcon} ${styles["ReportViewer__groupIcon--active"]}`
              : `${styles.ReportViewer__groupIcon}`
          }
          onClick={() => handleGroupClick(index)}
        >
          <GroupIcon />
        </div>
        {showFilter && (
          <div onClick={(event) => handleClickInsidePopupZone(event)}>
            <FilterPopup
              onLastFilterIndexDeselected={handleLastFilterIndexDelelected}
              primaryFilterColumn={primaryFilterColumn}
              largeViewData={largeViewData}
              list={filterList}
              left={showFilter.left}
              top={showFilter.top}
              onSelect={(item) => {
                let tempPrimaryFilterColumn = [...primaryFilterColumn];

                let itemIndex = tempPrimaryFilterColumn.findIndex(
                  (obj) => obj.description === item.description,
                );
                if (
                  (itemIndex === -1 &&
                    !tempPrimaryFilterColumn.length &&
                    item.selected) ||
                  (item.selected && item.index === primaryFilterColumn[0].index)
                ) {
                  tempPrimaryFilterColumn.push(item);
                  itemIndex = item.index;
                } else if (
                  !item.selected &&
                  item.index === primaryFilterColumn[0].index
                ) {
                  tempPrimaryFilterColumn.splice(itemIndex, 1);
                }
                let tempColumnFilters = [...columnFilters];
                let tempFilter = tempColumnFilters[item.index];
                let obj = tempFilter.find(
                  (o) => o.description === item.description,
                );
                obj.selected = item.selected;
                setPrimaryFilterColumn(tempPrimaryFilterColumn);
                setColumnFilters(tempColumnFilters);
                setLastFilterColumnIndexClicked(item.columnFiltersIndex);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  let tempColumnDataTypes = [];

  const TableHeadings = combinedReportFields.map((obj, index) => {
    tempColumnDataTypes[index] = obj.dataType;
    let width = columnWidths[index];
    if (index === combinedReportFields.length - 1 && !columnDataTypes.length) {
      setColumnDataTypes(tempColumnDataTypes);
    }
    return NormalTableHeadings(obj, width, index);
  });

  const getViewerData = () => {
    let columnHeaders = [];
    for (let colIndex = 0; colIndex < combinedReportFields.length; colIndex++) {
      if (!columnHeaders[colIndex]) {
        let fieldName;
        if (typeof combinedReportFields[colIndex] === "string") {
          fieldName = combinedReportFields[colIndex];
        } else {
          fieldName = getPreferredFieldName(
            combinedReportFields[colIndex]?.collection,
            combinedReportFields[colIndex]?.name,
            false,
            false,
            "header",
          );
        }
        columnHeaders.push(fieldName);
      }
    }

    let data = [];
    for (let i = 0; i < reportData.length; i++) {
      let row = [];
      let include = true;
      for (let colIndex = 0; colIndex < columnFilters.length; colIndex++) {
        if (isFiltered(i)) {
          row.push(columnFilters[colIndex][i].description);
        } else {
          include = false;
        }
      }
      if (include) {
        data.push(row);
      }
    }
    return { data: data, headers: columnHeaders };
  };

  if (showPDFViewer) {
    if (!reportData) {
      setShowPDFViewer(false);
      return;
    }

    let obj = getViewerData();

    return (
      <SubstitutePDFReportViewer
        user={user}
        title={t(reportObj.title)}
        headings={obj.headers.slice(0, 10)}
        groupedField={groupedField}
        data={groupedField === "No grouping" ? largeViewData : groupedData}
        collectionSequence={collectionSequence}
        onBack={() => setShowPDFViewer(false)}
        columnSums={columnSums}
        columnAverages={columnAverages}
        selectionRange={selectionRange}
        showAVGRow={showAVGRow}
        showSUMRow={showSUMRow}
        defaultFontSize="small"
      />
    );
  } else if (exportToExcel) {
    if (!reportData) {
      setExportToExcel(false);
      return;
    }
    let obj = getViewerData();
    return (
      <ReportExcelExporter
        title={t(reportObj.title)}
        headers={obj.headers}
        groupedField={groupedField}
        data={groupedField === "No grouping" ? largeViewData : groupedData}
        collectionSequence={collectionSequence}
        onBack={() => setExportToExcel(false)}
        columnSums={columnSums}
        columnAverages={columnAverages}
        selectionRange={selectionRange}
      />
    );
  } else if (fetchingData) {
    // return <WaitIndicator message={t("generating")} />;
  }

  const handleGroupSelection = (rowIndex) => {
    if (selectedGroupRowIndex === rowIndex && expansionActive) {
      setExpansionActive(false);
      setSelectedGroupRowIndex(-1);
    } else {
      setExpansionActive(true);
      setSelectedGroupRowIndex(rowIndex);
    }
  };

  const handleSelectedBatches = (arr) => {
    setSelectedBatches(arr);
  };

  // Browswer view
  const LargeView = (
    <div
      className={
        !showPDFViewer
          ? styles.ReportViewer__main
          : styles.ReportViewer__mainWithCustomPDF
      }
    >
      {showDateTimeRangePicker && (
        <div className={styles.ReportViewer__overlay}>
          <div className={styles.ReportViewer__modal}>
            <DateRangePickerComponent
              ranges={[selectionRange]}
              onChange={handleDateRangeSelect}
              rangeColors={["#089BAB"]}
              country={user?.defaultSite?.shippingAddress?.country}
            />
            <br />
            <Button
              labelName={t("continueButtonLabel")}
              isPrimary={true}
              onClick={() => {
                setShowDateTimeRangePicker(false);
                setReportData([]);
                if (dispensingOrderIdFromOrder) {
                  setEnteredBatchId(dispensingOrderIdFromOrder);
                } else {
                  setEnteredBatchId("-1");
                }
              }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className={styles.ReportViewer__row}>
          <ErrorBanner message={error} onClose={() => setError(null)} />
          <Spacer space={10} unit={"px"} />
        </div>
      )}

      {fetchingData && <WaitIndicator message={t("generating")} />}

      {!showPDFViewer && (
        <div className={styles.ReportViewer__header}>
          <div className={styles.ReportViewer__titleGroup}>
            <div className={styles.ReportViewer__backButton}>
              <img src={backIcon} onClick={onClose} alt="" />
            </div>
            <p className={styles.ReportViewer__reportTitle}>
              {t(reportObj.title)}
            </p>
            <Fragment>
              <button
                className={styles.ReportViewer__resetButtons}
                onClick={handleResetToDefault}
              >
                {t("resetLabel")}
              </button>
              <button
                className={styles.ReportViewer__resetButtons}
                onClick={handleNoFieldGrouping}
              >
                {t("noGroupsLabel")}
              </button>
              <button
                className={styles.ReportViewer__resetButtons}
                onClick={handleNoFilters}
              >
                {t("noFiltersLabel")}
              </button>
            </Fragment>
          </div>
          {!reportObj.ignoreDateRange && (
            <div className={styles.ReportViewer__dateRangeGroup}>
              <p className={styles.ReportViewer__dateRangeLabel}>
                {t("dateFilterLabel")}
              </p>
              <p
                className={styles.ReportViewer__dateRange}
                onClick={() => setShowDateTimeRangePicker(true)}
              >
                {getLocalizedDate(
                  selectionRange.startDate,
                  user?.defaultSite?.shippingAddress?.country,
                ) || ""}
                -
                {getLocalizedDate(
                  selectionRange.endDate,
                  user?.defaultSite?.shippingAddress?.country,
                ) || ""}
              </p>
            </div>
          )}
          {!!(selectableBatches.length && selectableBatches.length) && (
            <div className={customStyles.ReportViewer__selectBatchesContainer}>
              <MultiSelectDropDown
                value={selectedBatches}
                options={selectableBatches}
                fieldNames={{ value: "id", text: "label" }}
                onChanged={handleSelectedBatches}
                numberSelections={selectedBatches?.length}
                selectLabelHeading={t("selectBatches")}
                selectLabelStatus={`${selectedBatches?.length} selected`}
                selectAllLabel={t("selectAllBatches")}
                unselectAllLabel={t("unselectAllBatches")}
                noContentLabel={t("noRecordsFilter")}
              />
            </div>
          )}
          {initialDataLoaded && !selectableBatches.length && (
            <div
              className={customStyles.ReportViewer__noBatchesFoundMsgContainer}
            >
              <p className={customStyles.ReportViewer__noBatchesFoundMsg}>
                No batches found
              </p>
            </div>
          )}
          <div className={styles.ReportViewer__buttonGroup}>
            {runReport && reportObj?.fields?.length && (
              <div className={styles.ReportViewer__fileIconContainer}>
                <PDFIcon
                  className={styles.ReportViewer__fileIcon}
                  onClick={() => setShowPDFViewer(true)}
                />
              </div>
            )}
            {runReport && reportObj?.fields?.length && (
              <div className={styles.ReportViewer__fileIconContainer}>
                <ExcelIcon
                  className={styles.ReportViewer__fileIcon}
                  onClick={() => setExportToExcel(true)}
                />
              </div>
            )}
            <div className={styles.ReportViewer__runReportButtonContainer}>
              {(!isPreview || !reportObj.ignoreDateRange) && sites.length && (
                <Button
                  labelName={t("runReportLabel")}
                  minWidth={"123px"}
                  isPrimary={true}
                  onClick={() => handleRunReport(0, 0)}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div id="tableContainer" className={styles.ReportViewer__tableContainer}>
        <div
          style={{ width: tableWidthWithScroll }}
          className={
            !groupedField || groupedField === "No grouping"
              ? styles.ReportViewer__reportHeader
              : `${styles.ReportViewer__reportHeader} ${styles["ReportViewer__reportHeader--grouping"]}`
          }
        >
          {TableHeadings}
        </div>
        <div
          style={{
            width: tableWidthWithScroll,
            height: reportContainerHeight,
          }}
          className={styles.ReportViewer__reportDataScrollView}
        >
          {(!groupedField || groupedField === "No grouping") &&
            showTableData &&
            columnFilters &&
            columnFilters.length > 0 &&
            reportData &&
            largeViewData
              .filter((dataRow) => {
                if (dataRow) {
                  return selectedBatches?.includes(dataRow[12]);
                }
                return false;
              })
              .map((dataRow, index) => {
                return (
                  <div
                    key={index}
                    className={styles.ReportViewer__reportDataContainer1}
                  >
                    <div className={styles.ReportViewer__reportDataContainer2}>
                      {dataRow.map((datum, datumIndex) => {
                        let displayDatum = datum;
                        // date
                        if (datumIndex === 12 && datum && datum.includes(",")) {
                          datum = datum.split(",")[0];
                          dataRow[12] = datum;
                        }
                        if (typeof datum === "string") {
                          displayDatum = datum.substring(0, 750);
                          if (datum.length > 750) {
                            displayDatum += "...";
                          }
                        }
                        let width = columnWidths[datumIndex];
                        return (
                          <div
                            key={datumIndex}
                            className={styles.ReportViewer__reportDataText}
                            style={{
                              minWidth: width
                                ? `${width + addedWidthAdjustment}px`
                                : "",
                              maxWidth: width
                                ? `${width + addedWidthAdjustment}px`
                                : "",
                            }}
                          >
                            {displayDatum}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          {groupedField !== "No grouping" &&
            columnFilters &&
            columnFilters.length > 0 &&
            reportData &&
            groupedData.map((groupedRow, groupedRowIndex) => {
              let groupHeight;
              if (groupedRow.length > 1 && groupedRow.length * 46 <= 500) {
                groupHeight = `${groupedRow.length * 46}px`;
              } else if (groupedRow.length === 1) {
                groupHeight = "80px";
              } else {
                groupHeight = "500px";
              }

              return (
                <div
                  key={groupedRowIndex}
                  className={styles.ReportViewer__reportDataContainerGrouping}
                  style={{
                    overflowY:
                      groupedRowIndex === selectedGroupRowIndex
                        ? "auto"
                        : "hidden",
                    minHeight:
                      groupedRowIndex === selectedGroupRowIndex &&
                      expansionActive
                        ? groupHeight
                        : "80px",
                    maxHeight:
                      groupedRowIndex === selectedGroupRowIndex &&
                      expansionActive
                        ? "500px"
                        : "80px",
                    justifyContent:
                      groupedRowIndex === selectedGroupRowIndex &&
                      expansionActive
                        ? "flex-start"
                        : "center",
                    border:
                      groupedRowIndex === selectedGroupRowIndex &&
                      expansionActive
                        ? "1px solid #089bab"
                        : "1px solid #e8ecef",
                    cursor:
                      groupedData[groupedRowIndex].length > 1
                        ? "pointer"
                        : "default",
                  }}
                  onClick={
                    groupedData[groupedRowIndex].length > 1
                      ? () => {
                          handleGroupSelection(groupedRowIndex);
                        }
                      : () => {}
                  }
                >
                  {groupedRow
                    .map((dataRow, dataRowIndex) => {
                      return (
                        <div
                          key={dataRowIndex}
                          className={`${styles.ReportViewer__dataRow} ${styles["ReportViewer__dataRow--grouping"]}`}
                          style={{
                            borderBottom:
                              groupedRowIndex === selectedGroupRowIndex &&
                              expansionActive &&
                              dataRowIndex !== groupedRow.length - 1
                                ? "1px solid rgba(211, 211, 211, 0.5"
                                : "none",
                          }}
                        >
                          {groupedData[groupedRowIndex].length > 1 &&
                            dataRowIndex === 0 &&
                            (groupedRowIndex === selectedGroupRowIndex &&
                            expansionActive ? (
                              <div
                                className={
                                  styles.ReportViewer__expandOrCollapseIconContainer
                                }
                              >
                                <CollapseIcon />
                              </div>
                            ) : (
                              <div
                                className={
                                  styles.ReportViewer__expandOrCollapseIconContainer
                                }
                              >
                                <ExpandIcon />
                              </div>
                            ))}

                          {dataRow.map((datum, datumIndex) => {
                            let width = columnWidths[datumIndex];

                            return (
                              <div
                                key={datumIndex}
                                className={styles.ReportViewer__reportDataText}
                                style={{
                                  minWidth: width
                                    ? `${width + addedWidthAdjustment}px`
                                    : "",
                                  maxWidth: width
                                    ? `${width + addedWidthAdjustment}px`
                                    : "",
                                }}
                              >
                                {columnDataTypes.length &&
                                groupedDataColumnSums.length &&
                                groupedDataColumnSums.length ===
                                  groupedData.length &&
                                columnDataTypes[datumIndex] === "Number" &&
                                (!expansionActive ||
                                  selectedGroupRowIndex !== groupedRowIndex)
                                  ? groupedDataColumnSums[groupedRowIndex][
                                      datumIndex
                                    ]
                                  : datum}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                    .filter(
                      (filteredRow, index) =>
                        index === 0 ||
                        (groupedRowIndex === selectedGroupRowIndex &&
                          expansionActive),
                    )}
                </div>
              );
            })}
        </div>
        {(showAVGRow || showSUMRow) && (
          <div className={styles.ReportViewer__reportFooter}>
            {combinedReportFields.map((obj, index) => {
              let width = columnWidths[index];
              let sumValue;
              let averageValue;
              if (obj.sum || obj.average) {
                sumValue = Number(getColumnSumAndAverage(index).sum).toFixed(2);
                averageValue = Number(
                  getColumnSumAndAverage(index).average,
                ).toFixed(2);
              }
              return (
                <div
                  className={styles.ReportViewer__columnSumGroup}
                  key={index}
                >
                  {showSUMRow && (
                    <div
                      className={styles.ReportViewer__reportDataText}
                      style={{
                        minWidth: width
                          ? `${width + addedWidthAdjustment}px`
                          : "",
                        maxWidth: width
                          ? `${width + addedWidthAdjustment}px`
                          : "",
                        minHeight: "20px",
                      }}
                    >
                      <span
                        className={styles.ReportViewer__columnSumGroupHeading}
                      >
                        {index === 0 && "SUM"}
                      </span>
                      <span className={styles.ReportViewer__columnSumGroupData}>
                        {obj.sum ? sumValue : "--"}
                      </span>
                    </div>
                  )}
                  {showAVGRow && (
                    <div
                      className={styles.ReportViewer__reportDataText}
                      style={{
                        minWidth: width
                          ? `${width + addedWidthAdjustment}px`
                          : "",
                        maxWidth: width
                          ? `${width + addedWidthAdjustment}px`
                          : "",
                        minHeight: "20px",
                      }}
                    >
                      <span
                        className={styles.ReportViewer__columnSumGroupHeading}
                      >
                        {index === 0 && "AVG"}
                      </span>
                      <span className={styles.ReportViewer__columnSumGroupData}>
                        {obj.average ? averageValue : "--"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const MobileView = () => {
    let headingsMobile = combinedReportFieldsRegular.map((field) => {
      return field.name;
    });

    let dataMobile =
      reportData &&
      reportData.map((reportDataRow) => {
        return reportDataRow.map((reportDataRowItem) => reportDataRowItem);
      });

    return (
      <div className={styles.ReportViewer__main}>
        {showDateTimeRangePicker && (
          <div className={styles.ReportViewer__overlay}>
            <div className={styles.ReportViewer__modal}>
              <DateRangePickerComponent
                ranges={[selectionRange]}
                onChange={handleDateRangeSelect}
                rangeColors={["#089BAB"]}
                country={user?.defaultSite?.shippingAddress?.country}
              />
              <br />
              <Button
                labelName={t("continueButtonLabel")}
                isPrimary={true}
                onClick={() => setShowDateTimeRangePicker(false)}
              />
            </div>
          </div>
        )}
        {error && (
          <div className={styles.ReportViewer__row}>
            <ErrorBanner message={error} onClose={() => setError(null)} />
            <Spacer space={10} unit={"px"} />
          </div>
        )}
        <div className={styles.ReportViewer__header}>
          <div className={styles.ReportViewer__titleGroup}>
            <div className={styles.ReportViewer__backButton}>
              <img src={backIcon} onClick={onClose} alt="" />
            </div>
            <p className={styles.ReportViewer__reportTitle}>
              {t(reportObj.title)}
            </p>
          </div>
          {!reportObj.ignoreDateRange && (
            <div className={styles.ReportViewer__dateRangeGroup}>
              <p className={styles.ReportViewer__dateRangeLabel}>
                {t("dateFilterLabel")}
              </p>
              <p
                className={styles.ReportViewer__dateRange}
                onClick={() => setShowDateTimeRangePicker(true)}
              >
                {selectionRange.startDate?.toLocaleDateString() || ""}-
                {selectionRange.endDate?.toLocaleDateString() || ""}
              </p>
            </div>
          )}
        </div>
        <div className={styles.ReportViewer__buttonGroup}>
          <div className={styles.ReportViewer__runReportButtonContainer}>
            {(!isPreview || !reportObj.ignoreDateRange) && (
              <Button
                labelName={t("runReportLabel")}
                minWidth={"123px"}
                isPrimary={true}
                onClick={() => handleRunReport(0, 0)}
              />
            )}
          </div>
        </div>
        {runReport && (
          <ProductionReportCardList
            headings={headingsMobile}
            data={dataMobile}
          />
        )}
      </div>
    );
  };

  return smScreen ? MobileView() : LargeView;
};

export default SubstituteReportViewer;
